/** Va chạm tĩnh trên mặt phẳng XZ: hình tròn (cột, tượng, NPC) và hộp chữ nhật xoay được (tường, bậc). */
export interface CircleCollider { kind: 'circle'; x: number; z: number; r: number }
export interface BoxCollider { kind: 'box'; x: number; z: number; hw: number; hd: number; rot: number }
export type Collider = CircleCollider | BoxCollider;

export const circle = (x: number, z: number, r: number): CircleCollider => ({ kind: 'circle', x, z, r });
export const box = (x: number, z: number, w: number, d: number, rot = 0): BoxCollider => ({ kind: 'box', x, z, hw: w / 2, hd: d / 2, rot });

/** Đẩy một vòng tròn (người chơi) ra khỏi mọi collider. Trả về vị trí đã hiệu chỉnh. */
export function resolveCircle(px: number, pz: number, pr: number, colliders: Collider[]): { x: number; z: number; hit: boolean } {
  let x = px, z = pz, hit = false;
  for (let iter = 0; iter < 3; iter++) {
    let moved = false;
    for (const c of colliders) {
      if (c.kind === 'circle') {
        const dx = x - c.x, dz = z - c.z; const d = Math.hypot(dx, dz); const min = pr + c.r;
        if (d < min) { const n = d > 1e-5 ? 1 / d : 0; x = c.x + (d > 1e-5 ? dx * n : 1) * min; z = c.z + (d > 1e-5 ? dz * n : 0) * min; moved = hit = true; }
      } else {
        const cos = Math.cos(-c.rot), sin = Math.sin(-c.rot);
        const lx = (x - c.x) * cos - (z - c.z) * sin; const lz = (x - c.x) * sin + (z - c.z) * cos;
        const cx = Math.max(-c.hw, Math.min(c.hw, lx)); const cz = Math.max(-c.hd, Math.min(c.hd, lz));
        let dx = lx - cx, dz = lz - cz; let d = Math.hypot(dx, dz);
        if (d < pr) {
          if (d < 1e-5) { // tâm nằm trong hộp: đẩy ra cạnh gần nhất
            const ox = c.hw - Math.abs(lx), oz = c.hd - Math.abs(lz);
            if (ox < oz) { dx = Math.sign(lx) || 1; dz = 0; d = 0; const nlx = Math.sign(lx || 1) * (c.hw + pr); return toWorld(c, nlx, lz, true); }
            dx = 0; dz = Math.sign(lz) || 1; const nlz = Math.sign(lz || 1) * (c.hd + pr); return toWorld(c, lx, nlz, true);
          }
          const push = (pr - d) / d; const nlx = lx + dx * push, nlz = lz + dz * push;
          const w = toWorld(c, nlx, nlz, true); x = w.x; z = w.z; moved = hit = true;
        }
      }
    }
    if (!moved) break;
  }
  return { x, z, hit };
}

function toWorld(c: BoxCollider, lx: number, lz: number, hit: boolean) {
  const cos = Math.cos(c.rot), sin = Math.sin(c.rot);
  return { x: c.x + lx * cos - lz * sin, z: c.z + lx * sin + lz * cos, hit };
}

/** Kiểm tra đoạn thẳng (camera → nhân vật) có cắt collider tròn không – dùng cho camera collision rẻ. */
export function segmentHitsCircle(ax: number, az: number, bx: number, bz: number, c: CircleCollider): number | null {
  const dx = bx - ax, dz = bz - az; const fx = ax - c.x, fz = az - c.z;
  const a = dx * dx + dz * dz; const b = 2 * (fx * dx + fz * dz); const cc = fx * fx + fz * fz - c.r * c.r;
  const disc = b * b - 4 * a * cc; if (disc < 0 || a === 0) return null;
  const t = (-b - Math.sqrt(disc)) / (2 * a); return t >= 0 && t <= 1 ? t : null;
}
